/**
 * 子节点版本切换提交作用域输入。
 */
export interface ResolveAssemblyChildVersionApplyScopeInput {
  /** 当前父装配中的稳定目标节点 key。 */
  readonly targetNodeKey: string
  /** 目标节点的父装配节点 key。 */
  readonly parentNodeKey: string
  /** 目标是否来自当前根离线包内嵌的运行时子节点。 */
  readonly targetWasRuntimeEmbedded: boolean
  /** 目标是否在当前项目模型和运行时 hierarchy 中都不存在。 */
  readonly targetWasMissingFromProjectModel: boolean
}

/**
 * 子节点版本切换交给项目模型加载器的局部应用作用域。
 */
export type AssemblyChildVersionApplyScope = {
  /** 被替换的旧运行时子树根。 */
  readonly targetNodeKey: string
  /** 目标节点的父装配节点 key。 */
  readonly parentNodeKey: string
} | {
  /** 没有旧运行时对象、需要新增的节点根。 */
  readonly insertedNodeKey: string
  /** 目标节点的父装配节点 key。 */
  readonly parentNodeKey: string
}

/**
 * 解析子节点版本切换应使用替换还是插入语义。
 *
 * 设计说明：
 * 1. 离线包内嵌节点虽然尚未物化为 `ModelAssetNode`，场景中已经存在旧 Object3D、topology 与 BRep；
 * 2. 因此它必须走 scoped replace，才能清理旧运行时并把新 Mate 重绑到最终零件 occurrence；
 * 3. 只有项目模型和运行时 hierarchy 都不存在目标时，才允许使用 insert 语义。
 *
 * @param input 当前目标节点的结构与运行时存在状态。
 * @returns 可直接传给 `applyProjectModelLoadResult` 的局部作用域。
 */
export function resolveAssemblyChildVersionApplyScope(
  input: ResolveAssemblyChildVersionApplyScopeInput,
): AssemblyChildVersionApplyScope {
  if (input.targetWasMissingFromProjectModel && !input.targetWasRuntimeEmbedded) {
    return {
      insertedNodeKey: input.targetNodeKey,
      parentNodeKey: input.parentNodeKey,
    }
  }

  return {
    targetNodeKey: input.targetNodeKey,
    parentNodeKey: input.parentNodeKey,
  }
}
